import { clampFreeText } from './clamp-text.js';

/**
 * Artikel ohne Etikett, auch unbekannt, mit Foto (U17h, Plan 16 Abschnitt 4.10.9). Die Kasse erfasst Preis, wahlweise die
 * Verkäufernummer, einen kurzen Text und ein Foto. Die Leitung ordnet den Betrag später einem Verkäufer zu (U17i).
 *
 * Regel 4: Eine manuelle Zeile hat KEINEN Etikettencode. Sie bekommt nur eine lokale Kennung `manual:<…>` (höchstens 30 Zeichen,
 * damit sie in `SaleLine.code` passt). `classifyScannedCode` erkennt sie nie als Code, und der Server legt sie nie in
 * `items.label_code` oder `items.external_code` ab. Codes erzeugt weiter nur der Server.
 */

/** Anfang der lokalen Kennung einer manuellen Zeile. */
export const MANUAL_CODE_PREFIX = 'manual:';
/** Längste Kennung (Spalte `code` im Protokoll). */
export const MANUAL_CODE_MAX_LENGTH = 30;
/** Längster Text zur Zeile (Zeichen); kurz, ohne Personendaten. */
export const MANUAL_NOTE_MAX = 300;
/** Größte Verkäufernummer, die die Kasse annimmt. */
export const MANUAL_SELLER_NUMBER_MAX = 999_999;

/** Foto: auf dem Gerät verkleinert (lange Seite höchstens 1280 px, JPEG Qualität 0,7), Obergrenze 400 KB. */
export const PHOTO_MAX_SIDE_PX = 1280;
export const PHOTO_JPEG_QUALITY = 0.7;
export const PHOTO_TARGET_BYTES = 300 * 1024;
export const PHOTO_MAX_BYTES = 400 * 1024;
/** Höchstens so viele Fotos je Kasse in 24 Stunden. */
export const PHOTOS_PER_REGISTER_PER_DAY = 100;
/** Platz auf dem Gerät für noch nicht hochgeladene Fotos (Bytes). */
export const PHOTO_LOCAL_LIMIT_BYTES = 30 * 1024 * 1024;

export interface ManualLinePayload {
  sellerNumber?: number;
  note?: string;
  photoId?: string;
}

/** Ist das die lokale Kennung einer manuellen Zeile? */
export function isManualLineCode(code: string): boolean {
  return (
    code.startsWith(MANUAL_CODE_PREFIX) &&
    code.length > MANUAL_CODE_PREFIX.length &&
    code.length <= MANUAL_CODE_MAX_LENGTH
  );
}

/**
 * Lokale Kennung aus einer UUID: `manual:` plus so viele Hexziffern der UUID, wie in 30 Zeichen passen (23). Zwei manuelle
 * Zeilen im Korb kommen sich so nie in die Quere.
 */
export function manualLineCode(uuid: string): string {
  const hex = uuid.replace(/-/g, '').toLowerCase();
  return `${MANUAL_CODE_PREFIX}${hex.slice(0, MANUAL_CODE_MAX_LENGTH - MANUAL_CODE_PREFIX.length)}`;
}

/** Kürzt den Text auf `MANUAL_NOTE_MAX` Zeichen; leer ergibt `undefined`. */
export function clampManualNote(text: string | undefined): string | undefined {
  return clampFreeText(text, MANUAL_NOTE_MAX);
}

/** Gültige Verkäufernummer der Eingabe (ganze Zahl von 1 bis `MANUAL_SELLER_NUMBER_MAX`). */
export function isManualSellerNumber(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= MANUAL_SELLER_NUMBER_MAX;
}

/** Ist es ein JPEG? Prüft die Magic Bytes (FF D8 FF) und das Ende (FF D9). */
export function looksLikeJpeg(data: Uint8Array): boolean {
  return (
    data.length >= 4 &&
    data[0] === 0xff &&
    data[1] === 0xd8 &&
    data[2] === 0xff &&
    data[data.length - 2] === 0xff &&
    data[data.length - 1] === 0xd9
  );
}

/** Zeilentitel in Warenkorb und Verlauf: „Ohne Etikett · V17 · 6,00 €“ (ohne Verkäufer: „Ohne Etikett · 6,00 €“). */
export function manualLineTitle(sellerNumber: number | undefined): string {
  return sellerNumber === undefined ? 'Ohne Etikett' : `Ohne Etikett · V${String(sellerNumber)}`;
}
